import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Truck, Users, Shield, DollarSign, ArrowRight, CheckCircle2, Star, Menu, X, Award, TrendingUp, Clock, ThumbsUp, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

// Animated counter hook
function useCountUp(end, duration = 2000, startOnView = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const animateCount = () => {
      const startTime = Date.now();
      const animate = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        // Easing function for smooth deceleration
        const easeOut = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(easeOut * end));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    };

    if (!startOnView) {
      animateCount();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
          animateCount();
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasStarted, end, duration, startOnView]);

  return { count, ref };
}

// Animated Counter Component
function AnimatedStat({ end, suffix = '', prefix = '', label, icon: Icon, gradient }) {
  const { count, ref } = useCountUp(end, 2000);
  
  return (
    <div ref={ref} className="text-center group">
      <div className={`w-16 h-16 mx-auto ${gradient} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
        <Icon className="h-8 w-8 text-white" />
      </div>
      <div className="text-3xl font-black text-slate-900">
        {prefix}{count.toLocaleString()}{suffix}
      </div>
      <div className="text-slate-600 font-medium">{label}</div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const features = [
    { icon: Truck, title: 'One-Stop Shop', desc: 'Household goods, cargo, or freight — individuals and companies alike find everything here.' },
    { icon: Shield, title: 'Broker-Free Platform', desc: 'Connect directly with carriers. No middlemen, no hidden fees, just transparent service.' },
    { icon: DollarSign, title: 'Free to Join', desc: 'Only pay $5 transaction fee when both parties accept a job. No subscriptions.' },
    { icon: Users, title: 'Driver Job Board', desc: 'CDL and non-CDL drivers can find employment opportunities directly.' },
  ];

  const userTypes = [
    { title: 'Customers', desc: 'Individual or company? Post your moving jobs for household goods, cargo, or freight and receive quotes from carriers.', link: '/auth?role=customer' },
    { title: 'Carriers', desc: 'Find customers directly without broker fees. Bid on jobs and hire qualified drivers for your fleet.', link: '/auth?role=carrier' },
    { title: 'Drivers', desc: 'Create your profile and apply to driving positions nationwide. CDL and non-CDL opportunities available.', link: '/auth?role=driver' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/jobs" className="nav-link">Browse Jobs</Link>
              <Link to="/carriers" className="nav-link">Find Carriers</Link>
              <Link to="/driver-jobs" className="nav-link">Driver Jobs</Link>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="btn-accent" data-testid="dashboard-btn">
                  Dashboard
                </Button>
              ) : (
                <Button onClick={() => navigate('/auth')} className="btn-accent" data-testid="get-started-btn">
                  Get Started
                </Button>
              )}
            </div>

            {/* Mobile menu button */}
            <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4">
            <div className="flex flex-col gap-4">
              <Link to="/jobs" className="nav-link py-2">Browse Jobs</Link>
              <Link to="/carriers" className="nav-link py-2">Find Carriers</Link>
              <Link to="/driver-jobs" className="nav-link py-2">Driver Jobs</Link>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="btn-accent w-full">Dashboard</Button>
              ) : (
                <Button onClick={() => navigate('/auth')} className="btn-accent w-full">Get Started</Button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1633521248898-19cbbf9ead88?crop=entropy&cs=srgb&fm=jpg&q=85')` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight animate-fade-in">
              Your One-Stop Shop for<br />
              <span className="text-orange-500">Moving Anything</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-200 max-w-2xl animate-fade-in animate-delay-100">
              Whether you&apos;re an individual or a company, True Choice Enterprise connects you directly with trucking carriers. Household goods, cargo, or freight — we&apos;ve got you covered. Broker-free. Transparent. Reliable.
            </p>
            <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 animate-fade-in animate-delay-150">
              <p className="text-white font-medium text-center">
                🏠 Household Moves &nbsp;•&nbsp; 📦 Cargo Shipping &nbsp;•&nbsp; 🚛 Freight Transport
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 animate-fade-in animate-delay-200">
              <Button 
                onClick={() => navigate('/auth?role=customer')} 
                className="btn-accent text-lg px-8 py-4"
                data-testid="post-job-btn"
              >
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => navigate('/carriers')} 
                className="btn-secondary text-lg px-8 py-4"
                data-testid="browse-carriers-btn"
              >
                Browse Carriers
              </Button>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-6 text-white/80 animate-fade-in animate-delay-300">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>No Broker Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>Individuals &amp; Companies</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>Free to Join</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat 
              end={500} 
              suffix="+" 
              label="Verified Carriers" 
              icon={Truck} 
              gradient="bg-gradient-to-br from-orange-500 to-orange-600 shadow-orange-500/25"
            />
            <AnimatedStat 
              end={10000} 
              suffix="+" 
              label="Jobs Completed" 
              icon={CheckCircle2} 
              gradient="bg-gradient-to-br from-green-500 to-green-600 shadow-green-500/25"
            />
            <AnimatedStat 
              end={98} 
              suffix="%" 
              label="Customer Satisfaction" 
              icon={ThumbsUp} 
              gradient="bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-500/25"
            />
            <AnimatedStat 
              end={0} 
              prefix="$" 
              label="Broker Fees" 
              icon={Award} 
              gradient="bg-gradient-to-br from-purple-500 to-purple-600 shadow-purple-500/25"
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">What Our Customers Say</h2>
            <p className="mt-4 text-lg text-slate-600">Real reviews from businesses and individuals who trust us</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-slate-700 mb-6 leading-relaxed">
                &quot;Finally, a platform that cuts out the middleman! We saved over $5,000 on our warehouse relocation by working directly with carriers. The quote comparison feature is a game-changer.&quot;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  MJ
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Michael Johnson</div>
                  <div className="text-sm text-slate-500">Operations Manager, TechCorp Inc.</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-slate-700 mb-6 leading-relaxed">
                &quot;As a carrier, this platform has transformed our business. We get direct access to customers, no broker fees eating into our margins. Our bookings increased 40% in just 3 months!&quot;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  SR
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Sarah Rodriguez</div>
                  <div className="text-sm text-slate-500">Owner, Rodriguez Trucking LLC</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`h-5 w-5 ${i < 5 ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
                ))}
              </div>
              <p className="text-slate-700 mb-6 leading-relaxed">
                &quot;Moving my family across state lines was stressful enough. True Choice Enterprise made finding a reliable mover so easy. Got 5 quotes in 24 hours and picked the best one!&quot;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  DW
                </div>
                <div>
                  <div className="font-semibold text-slate-900">David Williams</div>
                  <div className="text-sm text-slate-500">Homeowner, Austin TX</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA under testimonials */}
          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/auth')} 
              className="btn-accent text-lg px-8 py-4"
              data-testid="testimonials-cta-btn"
            >
              Join 10,000+ Happy Customers <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">How It Works</h2>
            <p className="mt-4 text-lg text-slate-600">Get started in 3 simple steps</p>
          </div>
          
          <div className="relative">
            {/* Connection line - hidden on mobile */}
            <div className="hidden md:block absolute top-24 left-1/2 transform -translate-x-1/2 w-2/3 h-1 bg-gradient-to-r from-orange-200 via-orange-400 to-orange-200 rounded-full" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
              {/* Step 1 */}
              <div className="relative text-center group">
                <div className="relative z-10">
                  <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-xl border-2 border-orange-100 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:border-orange-400 transition-all duration-300">
                    <FileText className="h-10 w-10 text-orange-500" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    1
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Post Your Job</h3>
                <p className="text-slate-600 max-w-xs mx-auto">
                  Describe what you need moved, pickup and delivery locations, and your preferred dates.
                </p>
              </div>

              {/* Step 2 */}
              <div className="relative text-center group">
                <div className="relative z-10">
                  <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-xl border-2 border-orange-100 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:border-orange-400 transition-all duration-300">
                    <DollarSign className="h-10 w-10 text-orange-500" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    2
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Compare Quotes</h3>
                <p className="text-slate-600 max-w-xs mx-auto">
                  Receive competitive quotes directly from verified carriers. Chat with them to discuss details.
                </p>
              </div>

              {/* Step 3 */}
              <div className="relative text-center group">
                <div className="relative z-10">
                  <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-xl border-2 border-orange-100 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:border-orange-400 transition-all duration-300">
                    <Truck className="h-10 w-10 text-orange-500" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    3
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Get Moving!</h3>
                <p className="text-slate-600 max-w-xs mx-auto">
                  Accept the best quote and your carrier handles the rest. Track progress in real-time.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Button 
              onClick={() => navigate('/auth?role=customer')} 
              className="btn-accent text-lg px-8 py-4"
              data-testid="how-it-works-cta-btn"
            >
              Post Your First Job Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-sm text-slate-500">No credit card required to get started</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Why Choose True Choice Enterprise?</h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              We&apos;re revolutionizing the trucking industry by connecting customers directly with carriers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="card-marketing text-center">
                <div className="w-14 h-14 mx-auto bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="h-7 w-7 text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Types Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Join as a...</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {userTypes.map((type, i) => (
              <div key={i} className="card-marketing group hover:border-orange-500 transition-all">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{type.title}</h3>
                <p className="text-slate-600 mb-6">{type.desc}</p>
                <Button 
                  onClick={() => navigate(type.link)} 
                  className="btn-primary w-full group-hover:bg-orange-500"
                  data-testid={`join-${type.title.toLowerCase()}-btn`}
                >
                  Join as {type.title} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Driver Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Looking for <span className="text-orange-500">Driving Opportunities?</span>
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Create your driver profile and connect directly with carriers looking to hire CDL and non-CDL drivers. Get discovered by top trucking companies across the nation.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <span>Build your professional profile</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <span>Apply directly to driving positions</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                  <span>Browse available driving positions</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/auth?role=driver')} 
                className="btn-accent"
                data-testid="driver-signup-btn"
              >
                Create Driver Profile <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1655628591001-ae40b826ae24?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Professional truck driver"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-lg text-slate-600">One flat rate for all users. No hidden fees.</p>
          </div>
          
          <div className="max-w-md mx-auto">
            <div className="card-marketing text-center border-2 border-orange-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                FREE TO JOIN
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Platform Access</h3>
              <div className="mt-6 mb-4">
                <span className="text-5xl font-black text-green-600">FREE</span>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-slate-700">
                  <strong className="text-orange-600">$5.00 Transaction Fee</strong><br/>
                  Automatically charged to both parties when a job is accepted
                </p>
              </div>
              <ul className="space-y-4 text-left mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Unlimited job postings</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Direct carrier communication</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Quote management</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>Bill of Lading automation</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/auth')} 
                className="btn-accent w-full"
                data-testid="subscribe-free-btn"
              >
                Get Started Free
              </Button>
              <p className="mt-4 text-sm text-slate-500">
                Pay only when jobs are accepted
              </p>
            </div>

            {/* Premium Plan */}
            <div className="card-marketing text-center border-2 border-purple-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                BEST VALUE
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Premium</h3>
              <div className="mt-6 mb-4">
                <span className="text-5xl font-black text-purple-600">$49.99</span>
                <span className="text-slate-500">/month</span>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-purple-700 font-semibold">
                  NO TRANSACTION FEES<br/>
                  <span className="font-normal">Unlimited jobs with zero additional charges</span>
                </p>
              </div>
              <ul className="space-y-4 text-left mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <span>Everything in Free, plus:</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <span><strong>$0 transaction fees</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <span>Priority job listings</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-purple-500 flex-shrink-0" />
                  <span>Premium support</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/auth?plan=premium')} 
                className="bg-purple-500 hover:bg-purple-600 text-white w-full"
                data-testid="subscribe-premium-btn"
              >
                Go Premium
              </Button>
              <p className="mt-4 text-sm text-slate-500">
                Save money with 10+ jobs/month
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
                <span className="text-xl font-bold">True Choice Enterprise</span>
              </div>
              <p className="text-slate-400">
                Connecting customers directly with verified trucking carriers. Broker-free logistics.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">For Customers</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/jobs" className="hover:text-white">Post a Job</Link></li>
                <li><Link to="/carriers" className="hover:text-white">Find Carriers</Link></li>
                <li><Link to="/auth?role=customer" className="hover:text-white">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">For Carriers</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/jobs" className="hover:text-white">Browse Jobs</Link></li>
                <li><Link to="/driver-jobs" className="hover:text-white">Post Driver Jobs</Link></li>
                <li><Link to="/auth?role=carrier" className="hover:text-white">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">For Drivers</h4>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/driver-jobs" className="hover:text-white">Find Jobs</Link></li>
                <li><Link to="/auth?role=driver" className="hover:text-white">Create Profile</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Legal Links */}
          <div className="mt-8 pt-8 border-t border-slate-800">
            <div className="flex flex-wrap justify-center gap-6 text-slate-400">
              <Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link>
              <Link to="/user-agreement" className="hover:text-white">FMCSA Compliance</Link>
              <span className="text-slate-600">|</span>
              <a href="mailto:support@truechoiceenterprise.com" className="hover:text-white">Contact Support</a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-400">
            <p className="text-sm mb-2">
              <strong className="text-orange-400">DISCLAIMER:</strong> True Choice Enterprise is a broker-free platform only. Use at your own risk. 
              We do not recommend or refer any persons or businesses and assume no responsibility for any loss or gain.
            </p>
            <p>&copy; {new Date().getFullYear()} True Choice Enterprise. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
